import type { NavigateFunction } from "react-router-dom";
import { toast } from "sonner";
import { openPortalTaskById } from "@/utils/openPortalTask";

/** Shown when we sign the patient in but can't auto-open their emailed task. */
const TASK_FALLBACK_MSG =
  "We couldn't open your form automatically. You'll find it on your dashboard.";

/** Resume dashboard task after login from /task deep link */
export const SESSION_PENDING_TASK_ID = "portalPendingTaskId";
export const SESSION_PENDING_FACILITY_KEY = "portalPendingFacilityKey";
export const SESSION_PENDING_PATIENT_KEY = "portalPendingPatientKey";

export function setPendingTaskResume(params: {
  taskId: number;
  facilityKey: string;
  patientKey: string;
}): void {
  sessionStorage.setItem(SESSION_PENDING_TASK_ID, String(params.taskId));
  sessionStorage.setItem(SESSION_PENDING_FACILITY_KEY, params.facilityKey);
  sessionStorage.setItem(SESSION_PENDING_PATIENT_KEY, params.patientKey);
}

export function clearPendingTaskResume(): void {
  sessionStorage.removeItem(SESSION_PENDING_TASK_ID);
  sessionStorage.removeItem(SESSION_PENDING_FACILITY_KEY);
  sessionStorage.removeItem(SESSION_PENDING_PATIENT_KEY);
}

export function getPendingTaskResume(): {
  taskId: number;
  facilityKey: string;
  patientKey: string;
} | null {
  const tid = sessionStorage.getItem(SESSION_PENDING_TASK_ID);
  const fk = sessionStorage.getItem(SESSION_PENDING_FACILITY_KEY);
  if (tid == null || fk == null) return null;
  const taskId = Number(tid);
  if (!Number.isFinite(taskId)) return null;
  const patientKey = sessionStorage.getItem(SESSION_PENDING_PATIENT_KEY) ?? "";
  return { taskId, facilityKey: fk, patientKey };
}

export type PatientListRow = {
  facilityID: string;
  patientID: string;
  facilityKey?: string;
  patientKey?: string;
};

function rowKeys(p: PatientListRow): { fk: string; pk: string } {
  const fk = (p.facilityKey && String(p.facilityKey).trim()) || String(p.facilityID);
  const pk = (p.patientKey && String(p.patientKey).trim()) || String(p.patientID);
  return { fk, pk };
}

/**
 * When /task stashed a real patientKey, select that patient from GetUserPatients without showing the picker.
 */
export function findPatientByPendingKey(
  patients: PatientListRow[],
  pending: { facilityKey: string; patientKey: string }
): PatientListRow | undefined {
  const wantPk = pending.patientKey.trim();
  if (!wantPk) return undefined;
  const wantFk = pending.facilityKey.trim();
  return patients.find((p) => {
    const { fk, pk } = rowKeys(p);
    return fk === wantFk && pk === wantPk;
  });
}

/**
 * After login with a selected patient, open the deep-linked task if session keys match.
 * When patientKey was unknown (e.g. reset link), only facilityKey must match.
 */
export async function tryResumePendingTask(
  navigate: NavigateFunction,
  patient: {
    facilityID: string;
    patientID: string;
    facilityKey?: string;
    patientKey?: string;
  }
): Promise<void> {
  const pending = getPendingTaskResume();
  if (!pending) {
    navigate("/", { replace: true });
    return;
  }
  clearPendingTaskResume();
  if (pending.taskId <= 0) {
    navigate("/", { replace: true });
    return;
  }
  const fk =
    (patient.facilityKey && String(patient.facilityKey).trim()) ||
    String(patient.facilityID);
  const pk =
    (patient.patientKey && String(patient.patientKey).trim()) ||
    String(patient.patientID);
  if (fk !== pending.facilityKey) {
    toast.info(TASK_FALLBACK_MSG);
    navigate("/", { replace: true });
    return;
  }
  if (pending.patientKey && pk !== pending.patientKey) {
    toast.info(TASK_FALLBACK_MSG);
    navigate("/", { replace: true });
    return;
  }
  const opened = await openPortalTaskById({
    navigate,
    facilityKey: fk,
    patientKey: pk,
    taskId: pending.taskId,
  });
  if (!opened) {
    toast.info(TASK_FALLBACK_MSG);
    navigate("/", { replace: true });
  }
}
